import { useEffect, useState } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Gallery from './components/Gallery';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import BookAppointment from './components/BookAppointment';
import Footer from './components/Footer';
import Navigation from './components/Navigation';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Set document title
    document.title = 'Nappy Rootz Barbershop - Premium Men\'s Grooming & Haircuts';
    
    // Set meta tags
    const metaTags = [
      { name: 'description', content: 'Experience precision grooming at Nappy Rootz Barbershop. Specializing in men\'s classic cuts, line ups, beard grooming, and kids cuts.' },
      { name: 'keywords', content: 'barbershop, men\'s haircut, beard grooming, line up, kids cuts, classic cut, men\'s grooming' },
      { property: 'og:title', content: 'Nappy Rootz Barbershop - Premium Men\'s Grooming & Haircuts' },
      { property: 'og:description', content: 'Experience precision grooming at Nappy Rootz Barbershop. Specializing in men\'s classic cuts, line ups, beard grooming, and kids cuts.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: import.meta.env.VITE_SITE_URL || 'https://example.com' },
      { property: 'og:image', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg` },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Nappy Rootz Barbershop - Premium Men\'s Grooming & Haircuts' },
      { name: 'twitter:description', content: 'Experience precision grooming at Nappy Rootz Barbershop. Specializing in men\'s classic cuts, line ups, beard grooming, and kids cuts.' },
      { name: 'twitter:image', content: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg` }
    ];

    metaTags.forEach(({ name, property, content }) => {
      const attribute = name ? 'name' : 'property';
      const value = name || property;
      let meta = document.querySelector(`meta[${attribute}="${value}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value!);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // Add JSON-LD schema
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BarberShop",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#barbershop`,
          "name": "Nappy Rootz Barbershop",
          "description": "Premium men's grooming and barbershop services specializing in classic cuts, line ups, beard grooming, and kids cuts.",
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "telephone": "+1-XXX-XXX-XXXX",
          "priceRange": "$$",
          "image": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg`,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Your Street Address",
            "addressLocality": "Your City",
            "addressRegion": "Your State",
            "postalCode": "Your ZIP",
            "addressCountry": "US"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "09:00",
              "closes": "19:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "09:00",
              "closes": "18:00"
            }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Barbershop Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Mens Classic Cut",
                  "description": "Traditional precision haircut with attention to detail"
                },
                "priceSpecification": {
                  "@type": "PriceSpecification",
                  "price": "50",
                  "priceCurrency": "USD"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Line Up",
                  "description": "Sharp edge-up and hairline refinement"
                },
                "priceSpecification": {
                  "@type": "PriceSpecification",
                  "price": "30",
                  "priceCurrency": "USD"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Beard Grooming",
                  "description": "Professional beard trimming and styling"
                },
                "priceSpecification": {
                  "@type": "PriceSpecification",
                  "price": "40",
                  "priceCurrency": "USD"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Kids Cuts",
                  "description": "Gentle and professional haircuts for children"
                }
              }
            ]
          }
        },
        {
          "@type": "WebSite",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "name": "Nappy Rootz Barbershop",
          "description": "Premium men's grooming and barbershop services",
          "publisher": {
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#barbershop`
          }
        },
        {
          "@type": "WebPage",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#webpage`,
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "name": "Nappy Rootz Barbershop - Premium Men's Grooming & Haircuts",
          "isPartOf": {
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`
          },
          "about": {
            "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#barbershop`
          },
          "description": "Experience precision grooming at Nappy Rootz Barbershop. Specializing in men's classic cuts, line ups, beard grooming, and kids cuts."
        }
      ]
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schemaData);
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Navigation isScrolled={isScrolled} />
      <Hero />
      <About />
      <Services />
      <Gallery />
      <Testimonials />
      <Contact />
      <BookAppointment />
      <Footer />
    </div>
  );
}
